import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { createCase } from "@/lib/api";
import { MAHKEME_TURLERI } from "@/lib/constants";

const INIT = {
  dava_no: "", mahkeme_adi: "", mahkeme_turu: "hukuk", muvekkil: "", karsi_taraf: "",
  konu: "", is_bedeli: "", odeme_tipi: "pesin", taksit_sayisi: "1",
};

export default function NewCaseDialog({ open, onOpenChange, onCreated }) {
  const [data, setData] = useState(INIT);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    if (!data.dava_no.trim() || !data.mahkeme_adi.trim()) {
      toast.error("Dava No ve Mahkeme Adı zorunludur");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...data,
        is_bedeli: parseFloat(data.is_bedeli || 0),
        taksit_sayisi: parseInt(data.taksit_sayisi || 1, 10),
      };
      const created = await createCase(payload);
      toast.success("Dava oluşturuldu");
      setData(INIT);
      onOpenChange(false);
      onCreated?.(created);
    } catch (e) {
      toast.error("Dava oluşturulamadı");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-xl" data-testid="new-case-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-amber-500">Yeni Dava Oluştur</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-1">
            <Label className="text-xs text-slate-400">Dava Esas Dosya No *</Label>
            <Input value={data.dava_no} onChange={(e) => update("dava_no", e.target.value)}
              placeholder="2024/142 E." className="bg-slate-800 border-slate-700 mt-1 font-mono" data-testid="input-dava-no" />
          </div>
          <div className="col-span-1">
            <Label className="text-xs text-slate-400">Mahkeme Adı *</Label>
            <Input value={data.mahkeme_adi} onChange={(e) => update("mahkeme_adi", e.target.value)}
              placeholder="İstanbul 3. Asliye Hukuk Mahkemesi" className="bg-slate-800 border-slate-700 mt-1" data-testid="input-mahkeme" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-400">Mahkeme Türü</Label>
            <Select value={data.mahkeme_turu} onValueChange={(v) => update("mahkeme_turu", v)}>
              <SelectTrigger className="bg-slate-800 border-slate-700 mt-1" data-testid="input-mahkeme-turu">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {MAHKEME_TURLERI.map((m) => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Müvekkil</Label>
            <Input value={data.muvekkil} onChange={(e) => update("muvekkil", e.target.value)}
              className="bg-slate-800 border-slate-700 mt-1" data-testid="input-muvekkil" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Karşı Taraf</Label>
            <Input value={data.karsi_taraf} onChange={(e) => update("karsi_taraf", e.target.value)}
              className="bg-slate-800 border-slate-700 mt-1" data-testid="input-karsi-taraf" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-400">Dava Konusu</Label>
            <Textarea value={data.konu} onChange={(e) => update("konu", e.target.value)}
              rows={2} className="bg-slate-800 border-slate-700 mt-1" data-testid="input-konu" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">İş Bedeli (₺)</Label>
            <Input type="number" value={data.is_bedeli} onChange={(e) => update("is_bedeli", e.target.value)}
              placeholder="0.00" className="bg-slate-800 border-slate-700 mt-1 font-mono" data-testid="input-is-bedeli" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Ödeme Tipi</Label>
            <Select value={data.odeme_tipi} onValueChange={(v) => update("odeme_tipi", v)}>
              <SelectTrigger className="bg-slate-800 border-slate-700 mt-1" data-testid="select-odeme-tipi"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="pesin">Peşin</SelectItem>
                <SelectItem value="taksit">Taksitli</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {data.odeme_tipi === "taksit" && (
            <div>
              <Label className="text-xs text-slate-400">Taksit Sayısı</Label>
              <Input type="number" min="1" value={data.taksit_sayisi}
                onChange={(e) => update("taksit_sayisi", e.target.value)}
                className="bg-slate-800 border-slate-700 mt-1 font-mono" data-testid="input-taksit" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300" data-testid="dialog-cancel-btn">İptal</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-slate-950" data-testid="dialog-save-btn">
            {saving ? "Kaydediliyor..." : "Oluştur"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
